import { Message } from '@/hooks/useChatBot'
import { cn } from '@/lib/utils'
import { Card, CardContent } from '@/components/ui/card'
import { Bot, User } from 'lucide-react'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'

interface ChatMessageProps {
  message: Message
}

export function ChatMessage({ message }: ChatMessageProps) {
  const isBot = message.role === 'bot'

  return (
    <div
      className={cn(
        'flex w-full gap-2 mb-4 animate-fade-in',
        isBot ? 'justify-start' : 'justify-end',
      )}
    >
      {isBot && (
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-900 flex items-center justify-center text-white">
          <Bot size={16} />
        </div>
      )}

      <div className="flex flex-col gap-2 max-w-[85%] sm:max-w-[80%]">
        <div
          className={cn(
            'px-4 py-3 rounded-2xl text-sm shadow-sm break-words',
            isBot
              ? 'bg-white text-slate-800 border border-slate-100 rounded-tl-none'
              : 'bg-primary text-primary-foreground rounded-tr-none',
          )}
        >
          <div dangerouslySetInnerHTML={{ __html: message.content }} />
        </div>

        {/* Carousel Rendering */}
        {message.type === 'carousel' && message.cards && (
          <ScrollArea className="w-full whitespace-nowrap pb-2 max-w-[calc(100vw-80px)] sm:max-w-[300px]">
            <div className="flex w-max space-x-3 p-1">
              {message.cards.map((card, idx) => (
                <Card
                  key={idx}
                  className="w-[180px] sm:w-[200px] flex-shrink-0 bg-white border border-slate-200 shadow-sm whitespace-normal"
                >
                  <CardContent className="p-3">
                    <h4 className="font-bold text-xs mb-1 text-slate-900">
                      {card.title}
                    </h4>
                    <p className="text-[10px] text-slate-500 leading-tight">
                      {card.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        )}
      </div>

      {!isBot && (
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground">
          <User size={16} />
        </div>
      )}
    </div>
  )
}
